import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LockKeyhole, Pencil, Plus, QrCode, Trash2 } from "lucide-react";
import QRCode from "qrcode";
import { api } from "../api";
import { useAuth } from "../auth";
import type { CompanyUnit, Vehicle, VehicleType } from "../types";

const statusLabel: Record<string, string> = {
  liberado: "Liberado",
  liberado_ressalva: "Liberado com ressalva",
  em_manutencao: "Em manutenção",
  inativo: "Inativo"
};

type VehicleForm = {
  vehicle_type_id: string;
  unit_id: string;
  identifier: string;
  plate: string;
  brand: string;
  model: string;
  year: string;
  odometer: string;
  responsible_name: string;
};

const emptyForm: VehicleForm = {
  vehicle_type_id: "",
  unit_id: "",
  identifier: "",
  plate: "",
  brand: "",
  model: "",
  year: "",
  odometer: "",
  responsible_name: ""
};

export function VehiclesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [types, setTypes] = useState<VehicleType[]>([]);
  const [units, setUnits] = useState<CompanyUnit[]>([]);
  const [unitFilter, setUnitFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [qr, setQr] = useState<{ image: string; vehicle: Vehicle; url: string } | null>(null);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState<VehicleForm>(emptyForm);

  async function load() {
    const [v, t, u] = await Promise.all([
      api<{ vehicles: Vehicle[] }>("vehicles"),
      api<{ vehicle_types: VehicleType[] }>("vehicle-types"),
      api<{ units: CompanyUnit[] }>("units")
    ]);
    setVehicles(v.vehicles);
    setTypes(t.vehicle_types);
    setUnits(u.units);
    setForm((current) => ({
      ...current,
      vehicle_type_id: current.vehicle_type_id || t.vehicle_types[0]?.id || "",
      unit_id: current.unit_id || ""
    }));
  }

  useEffect(() => {
    const legacyVehicleId = searchParams.get("vehicle");
    if (legacyVehicleId) {
      navigate("/inspecoes/nova/" + encodeURIComponent(legacyVehicleId), { replace: true });
      return;
    }
    load();
  }, []);

  function openNew() {
    if (!isAdmin) return;
    setEditing(null);
    setMessage("");
    setForm({ ...emptyForm, vehicle_type_id: types[0]?.id || "", unit_id: "" });
    setShowForm(true);
  }

  function openEdit(vehicle: Vehicle) {
    if (!isAdmin) return;
    setEditing(vehicle);
    setMessage("");
    setForm({
      vehicle_type_id: vehicle.vehicle_type_id,
      unit_id: vehicle.unit_id || "",
      identifier: vehicle.identifier,
      plate: vehicle.plate || "",
      brand: vehicle.brand || "",
      model: vehicle.model || "",
      year: vehicle.year ? String(vehicle.year) : "",
      odometer: vehicle.odometer != null ? String(vehicle.odometer) : "",
      responsible_name: vehicle.responsible_name || ""
    });
    setShowForm(true);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!isAdmin) return;
    setMessage("");

    const payload = {
      ...form,
      year: form.year ? Number(form.year) : null,
      odometer: form.odometer ? Number(form.odometer) : null
    };

    try {
      if (editing) {
        await api("vehicles/" + editing.id, { method: "PATCH", body: JSON.stringify(payload) });
        setMessage("Cadastro do veículo atualizado.");
      } else {
        await api("vehicles", { method: "POST", body: JSON.stringify(payload) });
        setMessage("Veículo cadastrado.");
      }
      setShowForm(false);
      setEditing(null);
      setForm({ ...emptyForm, vehicle_type_id: types[0]?.id || "", unit_id: "" });
      await load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível salvar o veículo.");
    }
  }

  async function setStatus(vehicle: Vehicle, operational_status: string) {
    if (!isAdmin) return;
    await api("vehicles/" + vehicle.id, { method: "PATCH", body: JSON.stringify({ operational_status }) });
    await load();
  }

  async function removeVehicle(vehicle: Vehicle) {
    if (!isAdmin) return;
    const confirmed = window.confirm(
      `Deseja retirar "${vehicle.identifier}" da frota ativa? O histórico de inspeções e relatórios será preservado.`
    );
    if (!confirmed) return;

    await api("vehicles/" + vehicle.id, { method: "DELETE" });
    setMessage("Veículo retirado da frota ativa. O histórico foi preservado.");
    await load();
  }

  async function showQr(vehicle: Vehicle) {
    const url = window.location.origin + "/inspecoes/nova/" + encodeURIComponent(vehicle.id);
    const image = await QRCode.toDataURL(url, { width: 360, margin: 2, errorCorrectionLevel: "M" });
    setQr({ image, vehicle, url });
  }

  return (
    <section>
      <div className="page-title">
        <div>
          <h1>Veículos e equipamentos</h1>
          <p>Cadastro, status operacional, QR Code e rastreabilidade da frota.</p>
        </div>
        {isAdmin && (
          <button className="primary" onClick={openNew}>
            <Plus size={17} /> Novo veículo
          </button>
        )}
      </div>

      {!isAdmin && (
        <div className="permission-note">
          <LockKeyhole size={18} />
          <div>
            <strong>Consulta da frota</strong>
            <span>Somente administradores podem cadastrar, editar, alterar status ou excluir veículos.</span>
          </div>
        </div>
      )}

      {message && <div className="alert">{message}</div>}

      {isAdmin && showForm && (
        <form className="panel form-grid" onSubmit={submit}>
          <div className="full-span row-between">
            <div>
              <span className="section-kicker">{editing ? "EDIÇÃO" : "CADASTRO"}</span>
              <h2>{editing ? "Editar veículo / equipamento" : "Novo veículo / equipamento"}</h2>
            </div>
            <button type="button" className="secondary" onClick={() => { setShowForm(false); setEditing(null); }}>
              Cancelar
            </button>
          </div>

          <label>Tipo
            <select value={form.vehicle_type_id} onChange={(e) => setForm({ ...form, vehicle_type_id: e.target.value })}>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>

          <label>Unidade atual
            <select value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })}>
              <option value="">Não definida</option>
              {units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
            </select>
            <small className="field-help">Somente o administrador pode alterar a unidade do veículo.</small>
          </label>

          <label>Identificação / Patrimônio
            <input required value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} />
          </label>

          <label>Placa
            <input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} />
          </label>

          <label>Marca
            <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
          </label>

          <label>Modelo
            <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
          </label>

          <label>Ano
            <input type="number" min="1900" max="2100" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
          </label>

          <label>Responsável / motorista fixo
            <input
              placeholder="Opcional"
              value={form.responsible_name}
              onChange={(e) => setForm({ ...form, responsible_name: e.target.value })}
            />
            <small className="field-help">Preencha quando o veículo possuir motorista ou responsável fixo.</small>
          </label>

          <label>KM / Horímetro de referência
            <input type="number" min="0" step="0.1" value={form.odometer} onChange={(e) => setForm({ ...form, odometer: e.target.value })} />
          </label>

          <div className="form-actions">
            <button className="primary" type="submit">{editing ? "Salvar alterações" : "Cadastrar veículo"}</button>
          </div>
        </form>
      )}

      <div className="panel unit-filter-bar">
        <div>
          <span className="section-kicker">UNIDADE</span>
          <strong>Filtrar frota</strong>
        </div>
        <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
          <option value="all">Todas as unidades</option>
          <option value="unassigned">Sem unidade definida</option>
          {units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
        </select>
      </div>

      <div className="table-wrap panel">
        <table>
          <thead>
            <tr>
              <th>Identificação</th>
              <th>Tipo</th>
              <th>Unidade</th>
              <th>Placa</th>
              <th>Responsável</th>
              <th>KM / Horímetro</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {vehicles
              .filter((v) => unitFilter === "all" ? true : unitFilter === "unassigned" ? !v.unit_id : v.unit_id === unitFilter)
              .map((v) => (
              <tr key={v.id} className={!v.active ? "inactive-row" : ""}>
                <td>
                  <b>{v.identifier}</b>
                  <small>{[v.brand, v.model, v.year].filter(Boolean).join(" · ")}</small>
                </td>
                <td>{v.type_name}</td>
                <td>
                  <span className={"unit-badge " + (v.unit_id ? "" : "unassigned")}>
                    {v.unit_name || "Não definida"}
                  </span>
                </td>
                <td>{v.plate || "—"}</td>
                <td>{v.responsible_name || "—"}</td>
                <td>{v.odometer ?? "—"}</td>
                <td><span className={"status " + v.operational_status}>{statusLabel[v.operational_status]}</span></td>
                <td className="actions">
                  <button className="secondary icon-action" onClick={() => showQr(v)} title="QR Code">
                    <QrCode size={16} /> QR
                  </button>

                  {isAdmin && v.active === 1 && (
                    <>
                      <button className="secondary icon-action" onClick={() => openEdit(v)}>
                        <Pencil size={15} /> Editar
                      </button>
                      {v.operational_status === "em_manutencao"
                        ? <button className="secondary" onClick={() => setStatus(v, "liberado")}>Liberar</button>
                        : <button className="danger-outline" onClick={() => setStatus(v, "em_manutencao")}>Manutenção</button>}
                      <button className="danger-outline icon-action" onClick={() => removeVehicle(v)}>
                        <Trash2 size={15} /> Excluir
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {!vehicles.length && <tr><td colSpan={8} className="empty">Nenhum veículo cadastrado.</td></tr>}
            {vehicles.length > 0 && vehicles.filter((v) => unitFilter === "all" ? true : unitFilter === "unassigned" ? !v.unit_id : v.unit_id === unitFilter).length === 0 && (
              <tr><td colSpan={8} className="empty">Nenhum veículo encontrado para esta unidade.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {qr && (
        <div className="modal-backdrop" onClick={() => setQr(null)}>
          <div className="modal vehicle-qr-modal" onClick={(e) => e.stopPropagation()}>
            <span className="section-kicker">ACESSO RÁPIDO À INSPEÇÃO</span>
            <h2>QR Code — {qr.vehicle.identifier}</h2>
            <p className="muted">
              Ao escanear, o inspetor ou administrador será direcionado para uma nova inspeção deste veículo.
              Se não estiver logado, fará o login primeiro e seguirá automaticamente para o checklist.
            </p>
            <img className="qr" src={qr.image} alt={"QR Code de " + qr.vehicle.identifier} />
            <div className="qr-vehicle-info">
              <strong>{qr.vehicle.identifier}</strong>
              <span>{qr.vehicle.plate || "Sem placa"} · {qr.vehicle.type_name || "Veículo"} · {qr.vehicle.unit_name || "Unidade não definida"}</span>
            </div>
            <button className="secondary full" onClick={() => setQr(null)}>Fechar</button>
          </div>
        </div>
      )}
    </section>
  );
}
